import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { checkInstall } from "../store/slices/installSlice";
import { fetchTemplates, fetchTemplate } from "../api/templatesApi";
import { syncAllAgeShieldShopMetafields } from "../helper/ageTemplateMetafield";

/**
 * After install/reinstall, push DB templates → Shopify shop metafields once per app load.
 * OAuth callback also syncs server-side; this covers edge cases where that step failed.
 */
export function useSyncShopMetafieldsAfterInstall(installChecked, installData) {
  const dispatch = useDispatch();
  const startedRef = useRef(false);

  useEffect(() => {
    if (!installChecked || !installData?.shop_info?.id || startedRef.current) return;
    startedRef.current = true;

    let cancelled = false;

    (async () => {
      let shopifyId = installData?.shop_info?.shopify_id;
      if (shopifyId == null || String(shopifyId).trim() === "") {
        try {
          const refreshed = await dispatch(checkInstall(true)).unwrap();
          shopifyId = refreshed?.data?.shop_info?.shopify_id;
        } catch {
          return;
        }
      }
      if (cancelled || shopifyId == null || String(shopifyId).trim() === "") return;

      try {
        const listRes = await fetchTemplates();
        const rows = listRes?.data?.templates ?? [];
        if (!rows.length) return;

        const loaded = await Promise.all(
          rows.map(async (row) => {
            const tr = await fetchTemplate(row.id);
            return tr?.data?.template ?? null;
          }),
        );
        const templatesDetailed = loaded.filter(Boolean);
        if (!templatesDetailed.length || cancelled) return;

        await syncAllAgeShieldShopMetafields(shopifyId, templatesDetailed);
      } catch (e) {
        console.warn("[useSyncShopMetafieldsAfterInstall]", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [installChecked, installData, dispatch]);
}
